/**
 * Frontend Google Drive API Service
 * 
 * This service handles all Google Drive operations directly from the frontend,
 * eliminating the need for backend Google Drive API calls and reducing server load.
 * 
 * Features:
 * - Direct Google Drive API access using user's OAuth token
 * - Intelligent batching and throttling to respect API limits
 * - Offline-first architecture with sync queue
 * - Automatic retry with exponential backoff
 * - Conflict resolution for concurrent edits
 * - Automatic token refresh on 401 responses
 */

import googleTokenRefreshService from './googleTokenRefreshService'
import tokenManager from './tokenManager'

class GoogleDriveAPI {
  constructor() {
    this.API_BASE = 'https://www.googleapis.com/drive/v3'
    this.accessToken = null
    this.refreshToken = null
    this.folderCache = new Map()
    this.syncQueue = []
    this.isSyncing = false
    this.lastSyncTime = null
    this.apiLimiter = new ApiLimiter()
    this.retryManager = new RetryManager()
    
    // Only initialize if not on onboarding page
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (!isOnboarding) {
      // Check if tokens are already available in localStorage
      const existingAccessToken = localStorage.getItem('cloudAccessToken')
      const existingRefreshToken = localStorage.getItem('cloudRefreshToken')
      
      if (existingAccessToken) {
        console.log('🔑 GoogleDriveAPI: Found existing tokens in localStorage, initializing immediately')
        // Initialize tokens immediately if they exist
        this.initializeTokens()
        this.enableInitialization()
        
        // CRITICAL: Ensure TokenManager has set the expiry by calling setGoogleDriveToken again
        // This handles the case where TokenManager hasn't finished loading yet
        setTimeout(() => {
          if (existingAccessToken && !localStorage.getItem('googleDriveTokenExpiry')) {
            console.log('⚠️ GoogleDriveAPI: No expiry found after initialization, forcing TokenManager to set it')
            tokenManager.setGoogleDriveToken(existingAccessToken)
          }
        }, 100)
      } else {
        console.log('🔒 GoogleDriveAPI: No existing tokens, waiting for CloudStorageContext to provide them...')
        // Listen for token refresh events
        this.setupTokenRefreshListener()
        
        // Listen for access token ready event from CloudStorageContext
        this.setupAccessTokenListener()
      }
    } else {
      console.log('🔒 GoogleDriveAPI: Onboarding detected, skipping initialization')
    }
    
    // Google Drive API endpoints
    this.UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3'
    
    // Prevent concurrent folder creation (race condition fix)
    this.folderCreationLocks = new Map()
    
    // App-specific folder structure (must match backend naming)
    this.APP_FOLDER_NAME = 'NoteSpark-Do-not-Delete-this'
    this.FOLDERS = {
      NOTES: 'notes',
      ATTACHMENTS: 'attachments', 
      FOLDERS: 'folders',
      DELETED: 'soft-delete'
    }
    
    this.appFolderId = null
    this.folderCache = new Map()
    this.isInitializingFolders = false
    this.folderInitPromise = null
    this.initialized = false // Track initialization status
    this._tokenReadyListenerAdded = false // Flag to prevent multiple listeners
  }

  /**
   * Enable initialization (useful after onboarding completion or when tokens become available)
   */
  enableInitialization() {
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (!isOnboarding) {
      // Initialize tokens from localStorage (they should be available now)
      this.initializeTokens()
      
      // Setup token refresh listener if not already set up
      if (!this._tokenReadyListenerAdded) {
        this.setupTokenRefreshListener()
        this._tokenReadyListenerAdded = true
      }
      
      console.log('✅ GoogleDriveAPI: Initialization enabled with tokens from CloudStorageContext')
    } else {
      console.log('🔒 GoogleDriveAPI: Still on onboarding page, initialization delayed')
    }
  }
  
  /**
   * Force check for tokens and initialize if available
   */
  forceTokenCheck() {
    console.log('🔍 GoogleDriveAPI: Force checking for tokens...')
    const currentAccessToken = localStorage.getItem('cloudAccessToken')
    const currentRefreshToken = localStorage.getItem('cloudRefreshToken')
    
    console.log('🔍 GoogleDriveAPI: Force check - accessToken:', !!currentAccessToken, 'refreshToken:', !!currentRefreshToken)
    
    if (currentAccessToken && currentAccessToken !== this.accessToken) {
      console.log('🔑 GoogleDriveAPI: Force check found new access token, initializing...')
      // Use TokenManager to set the token so expiry is also set
      tokenManager.setGoogleDriveToken(currentAccessToken)
      this.accessToken = currentAccessToken
      this.enableInitialization()
      return true
    }
    
    console.log('🔍 GoogleDriveAPI: Force check - no new tokens found')
    return false
  }

  /**
   * Initialize with access token from backend authentication
   */
  async initialize(accessToken) {
    // Set/refresh token first
    // Use TokenManager to set the token so expiry is also set
    tokenManager.setGoogleDriveToken(accessToken)
    this.accessToken = accessToken
    console.log('🔑 GoogleDriveAPI: Setting access token:', typeof accessToken === 'string' ? accessToken.substring(0, 20) + '...' : 'undefined')

    // Wait for a valid token (guards race at app start)
    console.log('⏳ GoogleDriveAPI: Waiting for valid token...')
    await this.waitForValidToken(5000)
    console.log('✅ GoogleDriveAPI: Token validation complete')

    // Try to setup folder structure with one silent retry on network failure
    console.log('🚀 GoogleDriveAPI: Starting folder structure initialization...')
    try {
      await this.ensureAppFolderStructure()
      console.log('✅ GoogleDriveAPI: Initialized with frontend-only sync')
      this.initialized = true
    } catch (err) {
      console.warn('⚠️ GoogleDriveAPI: Initial folder setup failed, retrying silently...', err?.message || err)
      // Silent retry after a short delay
      await new Promise(r => setTimeout(r, 1000))
      try {
        await this.ensureAppFolderStructure()
        console.log('✅ GoogleDriveAPI: Initialized on retry')
        this.initialized = true
      } catch (finalErr) {
        console.error('❌ GoogleDriveAPI: Initialization failed after retry (will wait for token-ready event):', finalErr?.message || finalErr)
        this.initialized = false
        // Attach a one-time listener to retry when a fresh token arrives
        if (!this._tokenReadyListenerAdded) {
          this._tokenReadyListenerAdded = true
          window.addEventListener('accessTokenReady', async (e) => {
            try {
              const newToken = e?.detail?.accessToken
              if (newToken && newToken !== this.accessToken) {
                this.updateAccessToken(newToken)
              }
              await this.ensureAppFolderStructure()
              console.log('✅ GoogleDriveAPI: Initialized after accessTokenReady event')
              this.initialized = true
            } catch (_) {
              // Keep silent; UI will surface reconnection banner via existing handlers
            }
          }, { once: true })
        }
      }
    }
  }

  /**
   * Update access token (for token refresh scenarios)
   */
  updateAccessToken(newAccessToken) {
    console.log('🔄 GoogleDriveAPI: Updating access token:', typeof newAccessToken === 'string' ? newAccessToken.substring(0, 20) + '...' : 'undefined')
    console.log('🔍 GoogleDriveAPI: Access token type:', typeof newAccessToken)
    console.log('🔍 GoogleDriveAPI: Access token value:', newAccessToken)
    // Use TokenManager to set the token so expiry is also set
    tokenManager.setGoogleDriveToken(newAccessToken)
    this.accessToken = newAccessToken
  }

  /**
   * Check if token should be proactively refreshed
   */
  shouldProactivelyRefreshToken() {
    try {
      const tokenExpiry = localStorage.getItem('googleDriveTokenExpiry')
      if (!tokenExpiry) {
        console.log('🔍 GoogleDriveAPI: No googleDriveTokenExpiry found in localStorage')
        // If we have a token but no expiry, assume it's valid and don't force refresh
        // This prevents unnecessary 401s when the expiry hasn't been set yet
        if (this.accessToken) {
          console.log('✅ GoogleDriveAPI: No expiry found but have access token, assuming valid')
          return false
        }
        return false
      }
      
      const expiryTime = new Date(tokenExpiry).getTime()
      const currentTime = Date.now()
      const timeUntilExpiry = expiryTime - currentTime
      
      console.log('🔍 GoogleDriveAPI: Token expiry check:', {
        tokenExpiry,
        expiryTime: new Date(expiryTime),
        currentTime: new Date(currentTime),
        timeUntilExpiryMs: timeUntilExpiry,
        timeUntilExpirySeconds: Math.round(timeUntilExpiry / 1000)
      })
      
      // CRITICAL FIX: Be much more aggressive with token refresh to prevent 401s
      // Refresh if token expires within 30 minutes (very aggressive to prevent 401s)
      const shouldRefresh = timeUntilExpiry < (30 * 60 * 1000)
      
      // ADDITIONAL CHECK: Also refresh tokens that are older than 15 minutes regardless of expiry
      // This addresses cases where Google invalidates tokens earlier than our calculated expiry
      const tokenAge = currentTime - (expiryTime - (45 * 60 * 1000)) // 45 minutes is our calculated lifetime
      const isTokenOld = tokenAge > (15 * 60 * 1000) // 15 minutes
      
      if (shouldRefresh || isTokenOld) {
        console.log('⏰ GoogleDriveAPI: Token should refresh:', {
          reason: shouldRefresh ? 'expires soon' : 'token is old',
          timeUntilExpiry: Math.round(timeUntilExpiry / 1000),
          tokenAge: Math.round(tokenAge / 1000),
          shouldRefresh,
          isTokenOld
        })
        return true
      } else {
        console.log('✅ GoogleDriveAPI: Token is still valid for', Math.round(timeUntilExpiry / 1000), 'seconds')
      }
      
      return false
    } catch (error) {
      console.warn('⚠️ GoogleDriveAPI: Error checking token expiry:', error.message)
      return false
    }
  }

  /**
   * Wait until a valid non-empty token is available or timeout
   */
  waitForValidToken(timeoutMs = 5000) {
    console.log('🔍 GoogleDriveAPI: waitForValidToken called, accessToken type:', typeof this.accessToken)
    console.log('🔍 GoogleDriveAPI: waitForValidToken called, accessToken value:', this.accessToken)
    
    if (this.accessToken && this.accessToken.length > 10) return Promise.resolve(true)
    
    console.log('⏳ GoogleDriveAPI: Waiting for valid token, current token:', {
      hasToken: !!this.accessToken,
      tokenLength: this.accessToken?.length,
      tokenPrefix: typeof this.accessToken === 'string' ? this.accessToken.substring(0, 20) + '...' : 'undefined'
    })
    
    return new Promise((resolve) => {
      let settled = false
      
             const onToken = (e) => {
         console.log('🔍 GoogleDriveAPI: onToken event received:', e?.detail)
         const t = e?.detail?.accessToken
         console.log('🔍 GoogleDriveAPI: onToken extracted token type:', typeof t)
         console.log('🔍 GoogleDriveAPI: onToken extracted token value:', t)
         
         if (t && t.length > 10) {
           this.updateAccessToken(t)
           settled = true
           window.removeEventListener('accessTokenReady', onToken)
           window.removeEventListener('googleDriveTokenRefreshed', onTokenRefresh)
           console.log('✅ GoogleDriveAPI: Token received during wait, resolving')
           resolve(true)
         }
       }
       
       const onTokenRefresh = (e) => {
         console.log('🔍 GoogleDriveAPI: onTokenRefresh event received:', e?.detail)
         const t = e?.detail?.accessToken
         console.log('🔍 GoogleDriveAPI: onTokenRefresh extracted token type:', typeof t)
         console.log('🔍 GoogleDriveAPI: onTokenRefresh extracted token value:', t)
         
         if (t && t.length > 10) {
           this.updateAccessToken(t)
           settled = true
           window.removeEventListener('accessTokenReady', onToken)
           window.removeEventListener('googleDriveTokenRefreshed', onTokenRefresh)
           console.log('✅ GoogleDriveAPI: Token refreshed during wait, resolving')
           resolve(true)
         }
       }
      
      window.addEventListener('accessTokenReady', onToken)
      window.addEventListener('googleDriveTokenRefreshed', onTokenRefresh)
      
      setTimeout(() => {
        if (!settled) {
          window.removeEventListener('accessTokenReady', onToken)
          window.removeEventListener('googleDriveTokenRefreshed', onTokenRefresh)
          const hasValidToken = !!(this.accessToken && this.accessToken.length > 10)
          console.log(`⏰ GoogleDriveAPI: Token wait timeout (${timeoutMs}ms), has valid token: ${hasValidToken}`)
          resolve(hasValidToken)
        }
      }, timeoutMs)
    })
  }

  /**
   * Initialize tokens from localStorage
   */
  initializeTokens() {
    try {
      const storedAccessToken = localStorage.getItem('cloudAccessToken')
      const storedRefreshToken = localStorage.getItem('cloudRefreshToken')
      
      console.log('🔍 GoogleDriveAPI: initializeTokens - storedAccessToken type:', typeof storedAccessToken)
      console.log('🔍 GoogleDriveAPI: initializeTokens - storedAccessToken value:', storedAccessToken)
      console.log('🔍 GoogleDriveAPI: initializeTokens - storedRefreshToken type:', typeof storedRefreshToken)
      console.log('🔍 GoogleDriveAPI: initializeTokens - storedRefreshToken value:', storedRefreshToken)
      
      // Use TokenManager to set the token so expiry is also set
      if (storedAccessToken) {
        tokenManager.setGoogleDriveToken(storedAccessToken)
        this.accessToken = storedAccessToken
        console.log('🔑 GoogleDriveAPI: Initialized with access token from localStorage')
        
        // CRITICAL FIX: Ensure expiry is set even if TokenManager didn't set it
        if (!localStorage.getItem('googleDriveTokenExpiry')) {
          console.log('⚠️ GoogleDriveAPI: No expiry found after TokenManager.setGoogleDriveToken, setting default expiry')
          const defaultExpiry = new Date(Date.now() + (45 * 60 * 1000)).toISOString() // 45 minutes
          localStorage.setItem('googleDriveTokenExpiry', defaultExpiry)
          console.log('✅ GoogleDriveAPI: Set default expiry:', defaultExpiry)
        }
      }
      if (storedRefreshToken) {
        this.refreshToken = storedRefreshToken
        console.log('🔑 GoogleDriveAPI: Initialized with refresh token from localStorage')
      }
    } catch (error) {
      console.warn('⚠️ GoogleDriveAPI: Failed to initialize tokens from localStorage:', error)
    }
  }

  /**
   * Setup listener for token refresh events
   */
  setupTokenRefreshListener() {
    console.log('🔧 GoogleDriveAPI: Setting up token refresh listener')
    
         window.addEventListener('googleDriveTokenRefreshed', (event) => {
       console.log('🔄 GoogleDriveAPI: Received googleDriveTokenRefreshed event:', event.detail)
       console.log('🔍 GoogleDriveAPI: Event detail type:', typeof event.detail)
       console.log('🔍 GoogleDriveAPI: Event detail keys:', Object.keys(event.detail || {}))
       
       const { accessToken, refreshToken } = event.detail || {}
       
       console.log('🔍 GoogleDriveAPI: Extracted accessToken type:', typeof accessToken)
       console.log('🔍 GoogleDriveAPI: Extracted accessToken value:', accessToken)
       console.log('🔍 GoogleDriveAPI: Extracted refreshToken type:', typeof refreshToken)
       console.log('🔍 GoogleDriveAPI: Extracted refreshToken value:', refreshToken)
       
       if (accessToken) {
         // Use TokenManager to set the token so expiry is also set
         tokenManager.setGoogleDriveToken(accessToken)
         this.accessToken = accessToken
         console.log('✅ GoogleDriveAPI: Access token updated from refresh event')
         console.log('🔍 GoogleDriveAPI: New access token length:', accessToken?.length)
         
         // CRITICAL: Force check for any other token updates in localStorage
         this.forceTokenCheck()
         
         // CRITICAL: Immediately update internal state to prevent stale token usage
         console.log('🔄 GoogleDriveAPI: Immediately updating internal token state after refresh event')
         this.accessToken = accessToken
         this.refreshToken = refreshToken || this.refreshToken
       } else {
         console.warn('⚠️ GoogleDriveAPI: No access token in refresh event')
       }
       
       if (refreshToken) {
         this.refreshToken = refreshToken
         console.log('✅ GoogleDriveAPI: Refresh token updated from refresh event')
         console.log('🔍 GoogleDriveAPI: New refresh token length:', refreshToken?.length)
         
         // CRITICAL: Also update localStorage to ensure consistency
         // Use TokenManager to set the refresh token so it's handled consistently
         tokenManager.setGoogleDriveRefreshToken(refreshToken)
       } else {
         console.warn('⚠️ GoogleDriveAPI: No refresh token in refresh event')
       }
     })
  }

  /**
   * Setup listener for access token ready event from CloudStorageContext
   */
  setupAccessTokenListener() {
    console.log('🔧 GoogleDriveAPI: Setting up access token ready listener')
    
    // Listen for accessTokenReady event from CloudStorageContext
    window.addEventListener('accessTokenReady', (event) => {
      console.log('🔑 GoogleDriveAPI: Received accessTokenReady event:', event.detail)
      console.log('🔍 GoogleDriveAPI: Event detail type:', typeof event.detail)
      console.log('🔍 GoogleDriveAPI: Event detail keys:', Object.keys(event.detail || {}))
      
      const { accessToken, userInfo } = event.detail || {}
      
      console.log('🔍 GoogleDriveAPI: Extracted accessToken type:', typeof accessToken)
      console.log('🔍 GoogleDriveAPI: Extracted accessToken value:', accessToken)
      
      if (accessToken) {
        // Use TokenManager to set the token so expiry is also set
        tokenManager.setGoogleDriveToken(accessToken)
        this.accessToken = accessToken
        console.log('✅ GoogleDriveAPI: Access token set from CloudStorageContext event')
        console.log('🔍 GoogleDriveAPI: Access token length:', accessToken?.length)
        
        // Now that we have a token, we can initialize
        this.enableInitialization()
      } else {
        console.warn('⚠️ GoogleDriveAPI: No access token in accessTokenReady event')
      }
    })
    
    // Also check if tokens are already available in localStorage
    const existingAccessToken = localStorage.getItem('cloudAccessToken')
    const existingRefreshToken = localStorage.getItem('cloudRefreshToken')
    
    console.log('🔍 GoogleDriveAPI: Constructor - existingAccessToken type:', typeof existingAccessToken)
    console.log('🔍 GoogleDriveAPI: Constructor - existingAccessToken value:', existingAccessToken)
    console.log('🔍 GoogleDriveAPI: Constructor - existingRefreshToken type:', typeof existingRefreshToken)
    console.log('🔍 GoogleDriveAPI: Constructor - existingRefreshToken value:', existingRefreshToken)
    
    if (existingAccessToken) {
      console.log('🔑 GoogleDriveAPI: Found existing access token in localStorage, using it')
      // Use TokenManager to set the token so expiry is also set
      tokenManager.setGoogleDriveToken(existingAccessToken)
      this.accessToken = existingAccessToken
      this.enableInitialization()
    } else if (existingRefreshToken) {
      console.log('🔑 GoogleDriveAPI: Found refresh token but no access token, waiting for CloudStorageContext')
    } else {
      console.log('🔑 GoogleDriveAPI: No tokens found in localStorage, waiting for CloudStorageContext')
    }
    
    // NEW: Listen for localStorage changes to detect when tokens are added
    this.setupLocalStorageListener()
  }
  
  /**
   * Setup listener for localStorage changes to detect token availability
   */
  setupLocalStorageListener() {
    console.log('🔧 GoogleDriveAPI: Setting up localStorage change listener')
    
    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', (event) => {
      if (event.key === 'cloudAccessToken' && event.newValue) {
        console.log('🔑 GoogleDriveAPI: Detected cloudAccessToken change in localStorage')
        // Use TokenManager to set the token so expiry is also set
        tokenManager.setGoogleDriveToken(event.newValue)
        this.accessToken = event.newValue
        this.enableInitialization()
      }
    })
    
    // Also check periodically for tokens (fallback for same-tab changes)
    this.tokenCheckInterval = setInterval(() => {
      const currentAccessToken = localStorage.getItem('cloudAccessToken')
      if (currentAccessToken && currentAccessToken !== this.accessToken) {
        console.log('🔑 GoogleDriveAPI: Detected new cloudAccessToken in localStorage via polling')
        // Use TokenManager to set the token so expiry is also set
        tokenManager.setGoogleDriveToken(currentAccessToken)
        this.accessToken = currentAccessToken
        this.enableInitialization()
        
        // Clear the interval once we have a token
        if (this.tokenCheckInterval) {
          clearInterval(this.tokenCheckInterval)
          this.tokenCheckInterval = null
        }
      }
    }, 1000) // Check every second
    
    // Clear interval after 30 seconds to avoid infinite checking
    setTimeout(() => {
      if (this.tokenCheckInterval) {
        clearInterval(this.tokenCheckInterval)
        this.tokenCheckInterval = null
        console.log('⏰ GoogleDriveAPI: Token check interval cleared after 30 seconds')
      }
    }, 30000)
  }

  /**
   * Create/verify app folder structure in Google Drive
   */
  async ensureAppFolderStructure() {
    console.log('🔍 GoogleDriveAPI: ensureAppFolderStructure called')
    console.log('🔍 GoogleDriveAPI: Current state:', {
      isInitializingFolders: this.isInitializingFolders,
      appFolderId: this.appFolderId,
      folderCacheSize: this.folderCache.size,
      expectedFolders: Object.keys(this.FOLDERS).length,
      hasAccessToken: !!this.accessToken,
      accessTokenLength: this.accessToken?.length
    })
    
    // Prevent concurrent folder initialization
    if (this.isInitializingFolders) {
      console.log('🔄 GoogleDriveAPI: Folder initialization already in progress, waiting...')
      return this.folderInitPromise
    }
    
    // Check if already initialized
    if (this.appFolderId && this.folderCache.size === Object.keys(this.FOLDERS).length) {
      console.log('✅ GoogleDriveAPI: Folder structure already initialized')
      return
    }
    
    console.log('🚀 GoogleDriveAPI: Starting new folder initialization...')
    this.isInitializingFolders = true
    this.folderInitPromise = this._initializeFolderStructure()
    
    try {
      await this.folderInitPromise
      console.log('✅ GoogleDriveAPI: Folder initialization completed successfully')
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Folder initialization failed:', error)
      throw error
    } finally {
      this.isInitializingFolders = false
      this.folderInitPromise = null
    }
  }
  
  async _initializeFolderStructure() {
    try {
      console.log('🚀 GoogleDriveAPI: Starting folder structure initialization...')
      console.log('🔍 GoogleDriveAPI: Using access token:', {
        hasToken: !!this.accessToken,
        tokenLength: this.accessToken?.length,
        tokenPrefix: this.accessToken?.substring(0, 20) + '...'
      })
      
      // Find or create main app folder
      console.log('🔍 GoogleDriveAPI: Creating/finding main app folder:', this.APP_FOLDER_NAME)
      this.appFolderId = await this.findOrCreateFolder(this.APP_FOLDER_NAME, null)
      console.log('📂 GoogleDriveAPI: App folder ID:', this.appFolderId)
      
      // Create subfolders
      for (const [key, folderName] of Object.entries(this.FOLDERS)) {
        console.log(`📁 GoogleDriveAPI: Ensuring subfolder: ${folderName}`)
        const folderId = await this.findOrCreateFolder(folderName, this.appFolderId)
        this.folderCache.set(key, folderId)
        console.log(`✅ GoogleDriveAPI: ${folderName} folder ready with ID: ${folderId}`)
      }
      
      console.log('✅ GoogleDriveAPI: App folder structure ready')
      console.log('📋 GoogleDriveAPI: Final folder cache:', Object.fromEntries(this.folderCache))
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Failed to setup folder structure:', error)
      console.error('🔍 GoogleDriveAPI: Error details:', {
        message: error?.message,
        stack: error?.stack,
        hasAccessToken: !!this.accessToken,
        accessTokenLength: this.accessToken?.length
      })
      // Clear partial state on failure
      this.appFolderId = null
      this.folderCache.clear()
      throw error
    }
  }

  /**
   * Find existing folder or create new one
   */
  async findOrCreateFolder(name, parentId = null) {
    const query = parentId 
      ? `name='${name}' and parents in '${parentId}' and mimeType='application/vnd.google-apps.folder'`
      : `name='${name}' and mimeType='application/vnd.google-apps.folder'`

    console.log(`🔍 GoogleDriveAPI: Searching for folder: ${name} (parent: ${parentId || 'root'})`)

    try {
      const searchResponse = await this.apiRequest(`${this.API_BASE}/files`, {
        q: query,
        fields: 'files(id, name)'
      })

      if (!searchResponse.ok) {
        const errorText = await searchResponse.text()
        console.error(`❌ GoogleDriveAPI: Search request failed for folder '${name}':`, {
          status: searchResponse.status,
          statusText: searchResponse.statusText,
          error: errorText
        })
        throw new Error(`Search request failed: ${searchResponse.status} ${searchResponse.statusText}`)
      }

      const searchResult = await searchResponse.json()
      console.log(`🔍 GoogleDriveAPI: Search response for '${name}':`, searchResult)

      if (searchResult.files && searchResult.files.length > 0) {
        if (searchResult.files.length > 1) {
          console.warn(`⚠️ GoogleDriveAPI: Found ${searchResult.files.length} folders named '${name}'. Using first one: ${searchResult.files[0].id}`)
        } else {
          console.log(`✅ GoogleDriveAPI: Found existing folder '${name}': ${searchResult.files[0].id}`)
        }
        return searchResult.files[0].id
      }

      // Create folder if not found
      console.log(`📁 GoogleDriveAPI: Creating new folder: ${name}`)
      const createData = {
        name,
        mimeType: 'application/vnd.google-apps.folder'
      }
      
      if (parentId) {
        createData.parents = [parentId]
      }

      console.log(`📁 GoogleDriveAPI: Creating folder with data:`, createData)

      const createResponse = await this.apiRequest(`${this.API_BASE}/files`, null, {
        method: 'POST',
        body: JSON.stringify(createData)
      })

      if (!createResponse.ok) {
        const errorText = await createResponse.text()
        console.error(`❌ GoogleDriveAPI: Create folder request failed for '${name}':`, {
          status: createResponse.status,
          statusText: createResponse.statusText,
          error: errorText
        })
        throw new Error(`Create folder request failed: ${createResponse.status} ${createResponse.statusText}`)
      }

      // Parse the response to get the created folder data
      const createResult = await createResponse.json()
      console.log(`🔍 GoogleDriveAPI: Create folder response for '${name}':`, createResult)
      
      if (!createResult.id) {
        console.error('❌ GoogleDriveAPI: Created folder response missing ID:', createResult)
        console.error('❌ GoogleDriveAPI: Response keys:', Object.keys(createResult))
        console.error('❌ GoogleDriveAPI: Response type:', typeof createResult)
        
        // Try to extract ID from different possible response formats
        let folderId = null
        if (createResult.files && createResult.files.length > 0) {
          folderId = createResult.files[0].id
        } else if (createResult.file && createResult.file.id) {
          folderId = createResult.file.id
        } else if (createResult.id) {
          folderId = createResult.id
        }
        
        if (folderId) {
          console.log(`✅ GoogleDriveAPI: Extracted folder ID from alternative response format: ${folderId}`)
          return folderId
        }
        
        throw new Error(`Failed to create folder '${name}': Response missing ID. Response: ${JSON.stringify(createResult)}`)
      }

      console.log(`✅ GoogleDriveAPI: Created folder '${name}': ${createResult.id}`)
      return createResult.id
      
    } catch (error) {
      console.error(`❌ GoogleDriveAPI: Error in findOrCreateFolder for '${name}':`, error)
      console.error('🔍 GoogleDriveAPI: Error details:', {
        message: error?.message,
        stack: error?.stack,
        name,
        parentId,
        hasAccessToken: !!this.accessToken
      })
      throw error
    }
  }

  /**
   * List folder metadata JSON files from the FOLDERS subfolder
   */
  async listFolderMetas() {
    try {
      const foldersFolderId = this.folderCache.get('FOLDERS')
      let query = `parents in '${foldersFolderId}' and mimeType='application/json' and trashed = false`

      const params = {
        q: query,
        fields: 'files(id, name, modifiedTime, trashed)',
        orderBy: 'modifiedTime desc',
        pageSize: 100
      }

      const resultResponse = await this.apiRequest(`${this.API_BASE}/files`, params)
      const result = await resultResponse.json()
      const files = (result.files || []).filter(f => !f.trashed)
      const metas = []
      for (const file of files) {
        try {
          const contentResponse = await this.apiRequest(`${this.API_BASE}/files/${file.id}`, { alt: 'media' })
          const content = await contentResponse.text()
          const meta = (typeof content === 'string') ? JSON.parse(content) : content
          metas.push({ ...meta, cloudFileId: file.id })
        } catch (e) {
          console.warn('GoogleDriveAPI: Failed to load folder meta content:', file.name, e)
        }
      }
      return metas
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Failed to list folder metas:', error)
      throw error
    }
  }

  /**
   * Get Drive file ID for a folder meta by folderId
   */
  async getFolderFileId(folderId) {
    try {
      // First, we need to get the folder data to find its name
      // Since we're searching by name, not by ID
      const foldersFolderId = this.folderCache.get('FOLDERS')
      if (!foldersFolderId) {
        console.log('❌ GoogleDriveAPI: Folders directory not found')
        return null
      }
      
      // We need to search through all folder meta files to find the one with this folderId
      // This is not efficient, but it's the correct approach given the current architecture
      const query = `parents in '${foldersFolderId}' and mimeType='application/json' and trashed = false`
      const searchResponse = await this.apiRequest(`${this.API_BASE}/files`, {
        q: query,
        fields: 'files(id, name)',
        pageSize: 100
      })
      
      const searchResult = await searchResponse.json()
      
      if (!searchResult.files || searchResult.files.length === 0) {
        console.log('🔍 GoogleDriveAPI: No folder meta files found')
        return null
      }
      
      // Search through each file to find the one containing this folderId
      for (const file of searchResult.files) {
        try {
          const contentResponse = await this.apiRequest(`${this.API_BASE}/files/${file.id}?alt=media`)
          const content = await contentResponse.text()
          const folderData = typeof content === 'string' ? JSON.parse(content) : content
          
          if (folderData.folderId === folderId) {
            console.log(`✅ GoogleDriveAPI: Found folder meta file for folderId ${folderId}: ${file.name}`)
            return file.id
          }
        } catch (error) {
          console.warn(`⚠️ GoogleDriveAPI: Failed to read folder meta file ${file.name}:`, error)
          // Continue searching other files
        }
      }
      
      console.log(`❌ GoogleDriveAPI: No folder meta file found for folderId: ${folderId}`)
      return null
      
    } catch (error) {
      console.error(`❌ GoogleDriveAPI: Error getting folder file ID for ${folderId}:`, error)
      return null
    }
  }

  /**
   * Create or update a folder meta JSON file in Drive with unique filename architecture
   */
  async saveFolderMeta(folder) {
    const foldersFolderId = this.folderCache.get('FOLDERS')
    
    // NEW ARCHITECTURE: Use folder name as filename (e.g., sensitive.json, pinned.json)
    const fileName = `${folder.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}.json`
    const lockKey = `folder_${folder.name.toLowerCase()}`
    
    // RACE CONDITION FIX: Use mutex to prevent concurrent creation of same folder
    if (this.folderCreationLocks.has(lockKey)) {
      console.log(`⏳ GoogleDriveAPI: Waiting for concurrent creation of "${folder.name}" to complete...`)
      return await this.folderCreationLocks.get(lockKey)
    }
    
    // Create a promise for this folder creation and store it as a lock
    const creationPromise = this._createFolderMetaInternal(folder, fileName, foldersFolderId)
    this.folderCreationLocks.set(lockKey, creationPromise)
    
    try {
      const result = await creationPromise
      return result
    } finally {
      // Always clean up the lock when done
      this.folderCreationLocks.delete(lockKey)
    }
  }

  /**
   * Internal method to handle actual folder creation logic
   */
  async _createFolderMetaInternal(folder, fileName, foldersFolderId) {
    const data = JSON.stringify(folder, null, 2)

    // Check if folder already exists by name (prevents duplicates)
    const existingFile = await this.findFolderMetaByName(folder.name)
    
    if (existingFile) {
      console.log(`⚠️ GoogleDriveAPI: Folder "${folder.name}" already exists, updating existing file`)
      // Update existing file instead of creating duplicate
      return this.apiRequest(`${this.UPLOAD_BASE}/files/${existingFile.id}`, null, {
        method: 'PATCH',
        body: data,
        headers: { 'Content-Type': 'application/json' }
      })
    }

    // If we have cloudFileId but no existing file found, still try to update
    if (folder.cloudFileId) {
      try {
        return await this.apiRequest(`${this.UPLOAD_BASE}/files/${folder.cloudFileId}`, null, {
          method: 'PATCH',
          body: data,
          headers: { 'Content-Type': 'application/json' }
        })
      } catch (error) {
        console.warn(`⚠️ GoogleDriveAPI: Failed to update by cloudFileId, creating new file`)
      }
    }

    // Create new file with unique name
    const metadata = {
      name: fileName,
      parents: [foldersFolderId],
      mimeType: 'application/json'
    }

    const boundary = 'boundary_' + Date.now().toString(36)
    const partDelimiter = `--${boundary}\r\n`
    const closeDelimiter = `\r\n--${boundary}--`
    let body = ''
    body += partDelimiter
    body += 'Content-Type: application/json; charset=UTF-8\r\n\r\n'
    body += JSON.stringify(metadata) + '\r\n'
    body += partDelimiter
    body += 'Content-Type: application/json; charset=UTF-8\r\n\r\n'
    body += data
    body += closeDelimiter

    const result = await this.apiRequest(`${this.UPLOAD_BASE}/files?uploadType=multipart`, null, {
      method: 'POST',
      body,
      headers: { 'Content-Type': `multipart/related; boundary=${boundary}` }
    })

    console.log(`✅ GoogleDriveAPI: Created folder metadata: ${folder.name} as ${fileName}`)
    return result
  }

  /**
   * Find folder metadata by folder name (prevents duplicates)
   */
  async findFolderMetaByName(folderName) {
    const foldersFolderId = this.folderCache.get('FOLDERS')
    if (!foldersFolderId) {
      return null
    }

    const fileName = `${folderName.toLowerCase().replace(/[^a-z0-9]/g, '_')}.json`
    
    // Search for file with this exact name in the folders directory
    const query = `name='${fileName}' and parents in '${foldersFolderId}' and mimeType='application/json'`
    
    try {
      const searchResponse = await this.apiRequest(`${this.API_BASE}/files`, {
        q: query,
        fields: 'files(id, name)'
      })

      const searchResult = await searchResponse.json()

      if (searchResult.files && searchResult.files.length > 0) {
        // If multiple files exist (shouldn't happen with new architecture), use the first one
        if (searchResult.files.length > 1) {
          console.warn(`⚠️ GoogleDriveAPI: Found ${searchResult.files.length} files for ${fileName}, using first one`)
        }
        return searchResult.files[0]
      }
    } catch (error) {
      console.error(`❌ GoogleDriveAPI: Error searching for ${fileName}:`, error)
    }

    return null
  }

  /**
   * Delete a folder meta file from Drive
   */
  async deleteFolderMeta(folderId) {
    try {
      console.log(`🗑️ GoogleDriveAPI: Deleting folder meta for folderId: ${folderId}`)
      
      const fileId = await this.getFolderFileId(folderId)
      if (!fileId) {
        console.log(`⚠️ GoogleDriveAPI: No file ID found for folderId ${folderId}, folder may not exist in Google Drive`)
        return true
      }
      
      console.log(`🗑️ GoogleDriveAPI: Deleting file ${fileId} from Google Drive for folderId ${folderId}`)
      
      await this.apiRequest(`${this.API_BASE}/files/${fileId}`, null, { method: 'DELETE' })
      
      console.log(`✅ GoogleDriveAPI: Successfully deleted folder meta file ${fileId} for folderId ${folderId}`)
      return true
      
    } catch (error) {
      console.error(`❌ GoogleDriveAPI: Failed to delete folder meta for folderId ${folderId}:`, error)
      throw error
    }
  }

  /**
   * Clean up duplicate folder metadata files and migrate to new naming system
   */
  async cleanupAndMigrateFolders() {
    if (!this.appFolderId) {
      console.log('❌ GoogleDriveAPI: App folder not initialized, cannot cleanup')
      return
    }

    console.log('🧹 GoogleDriveAPI: Starting folder cleanup and migration...')

    const foldersFolderId = this.folderCache.get('FOLDERS')
    if (!foldersFolderId) {
      console.log('❌ GoogleDriveAPI: Folders directory not found')
      return
    }

    // Get all JSON files in the folders directory
    const query = `parents in '${foldersFolderId}' and mimeType='application/json'`
    const searchResponse = await this.apiRequest(`${this.API_BASE}/files`, {
      q: query,
      fields: 'files(id, name, createdTime)'
    })

    const searchResult = await searchResponse.json()

    if (!searchResult.files || searchResult.files.length === 0) {
      console.log('✅ GoogleDriveAPI: No folder metadata files found')
      return
    }

    // Group files by folder type (based on content)
    const folderGroups = new Map()
    const filesToDelete = []

    for (const file of searchResult.files) {
      try {
        // Download and parse the file content
        const contentResponse = await this.apiRequest(`${this.API_BASE}/files/${file.id}?alt=media`)
        const content = await contentResponse.text()
        const folderData = typeof content === 'string' ? JSON.parse(content) : content
        
        const folderName = folderData.name
        const folderType = folderData.folderType || 'CUSTOM'
        
        // For default folders (PINNED, SENSITIVE), group by type
        // For custom folders, group by name
        const groupKey = ['PINNED', 'SENSITIVE'].includes(folderType) ? folderType : folderName
        
        if (!folderGroups.has(groupKey)) {
          folderGroups.set(groupKey, [])
        }
        
        folderGroups.get(groupKey).push({
          file,
          folderData,
          expectedFileName: `${folderName.toLowerCase().replace(/[^a-z0-9]/g, '_')}.json`
        })
      } catch (error) {
        console.error(`❌ GoogleDriveAPI: Error processing file ${file.name}:`, error)
      }
    }

    // Process each group
    for (const [groupKey, files] of folderGroups.entries()) {
      if (files.length <= 1) {
        // No duplicates, but check if filename needs migration
        const fileData = files[0]
        if (fileData.file.name !== fileData.expectedFileName) {
          console.log(`🔄 GoogleDriveAPI: Migrating ${fileData.file.name} to ${fileData.expectedFileName}`)
          // Rename the file to new naming convention
          await this.apiRequest(`${this.API_BASE}/files/${fileData.file.id}`, null, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: fileData.expectedFileName })
          })
        }
        continue
      }

      // Multiple files found - keep the oldest one and delete the rest
      console.warn(`⚠️ GoogleDriveAPI: Found ${files.length} duplicate files for ${groupKey}`)
      
      // Sort by creation time (oldest first)
      files.sort((a, b) => new Date(a.file.createdTime) - new Date(b.file.createdTime))
      
      const keepFile = files[0]
      const deleteFiles = files.slice(1)
      
      console.log(`✅ GoogleDriveAPI: Keeping oldest file: ${keepFile.file.name} (${keepFile.file.createdTime})`)
      
      // Rename the kept file to new naming convention if needed
      if (keepFile.file.name !== keepFile.expectedFileName) {
        console.log(`🔄 GoogleDriveAPI: Migrating ${keepFile.file.name} to ${keepFile.expectedFileName}`)
        await this.apiRequest(`${this.API_BASE}/files/${keepFile.file.id}`, null, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: keepFile.expectedFileName })
        })
      }
      
      // Mark duplicates for deletion
      for (const deleteFile of deleteFiles) {
        console.log(`🗑️ GoogleDriveAPI: Marking for deletion: ${deleteFile.file.name} (${deleteFile.file.createdTime})`)
        filesToDelete.push(deleteFile.file)
      }
    }

    // Delete duplicate files
    for (const file of filesToDelete) {
      try {
        await this.apiRequest(`${this.API_BASE}/files/${file.id}`, null, { method: 'DELETE' })
        console.log(`✅ GoogleDriveAPI: Deleted duplicate file: ${file.name}`)
      } catch (error) {
        console.error(`❌ GoogleDriveAPI: Error deleting file ${file.name}:`, error)
      }
    }

    console.log(`🎉 GoogleDriveAPI: Cleanup complete! Deleted ${filesToDelete.length} duplicate files`)
  }

  /**
   * Save note to Google Drive
   */
  async saveNote(note) {
    return this.addToSyncQueue({
      type: 'save_note',
      data: note,
      priority: note.isNew ? 'high' : 'normal'
    })
  }

  /**
   * Load notes from Google Drive
   */
  async loadNotes(options = {}) {
    const { 
      folderId = null, 
      pageSize = 50, 
      pageToken = null,
      modifiedAfter = null 
    } = options

    try {
      const notesFolderId = this.folderCache.get('NOTES')
      let query = `parents in '${notesFolderId}' and mimeType='application/json' and trashed = false`
      
      if (modifiedAfter) {
        query += ` and modifiedTime > '${modifiedAfter.toISOString()}'`
      }

      const params = {
        q: query,
        fields: 'nextPageToken, files(id, name, modifiedTime, size, trashed)',
        orderBy: 'modifiedTime desc',
        pageSize
      }

      if (pageToken) {
        params.pageToken = pageToken
      }

      const resultResponse = await this.apiRequest(`${this.API_BASE}/files`, params)
      const result = await resultResponse.json()
      
      // Load note contents in parallel (with throttling)
      const activeFiles = (result.files || []).filter(f => !f.trashed)
      const notes = await this.loadNoteContents(activeFiles)
      
      return {
        notes,
        nextPageToken: result.nextPageToken,
        hasMore: !!result.nextPageToken
      }
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Failed to load notes:', error)
      throw error
    }
  }

  /**
   * Load note contents in parallel with throttling
   */
  async loadNoteContents(fileInfos) {
    const batchSize = 5 // Process 5 files at a time to respect API limits
    const notes = []

    for (let i = 0; i < fileInfos.length; i += batchSize) {
      const batch = fileInfos.slice(i, i + batchSize)
      const batchPromises = batch.map(file => this.loadNoteContent(file))
      const batchResults = await Promise.allSettled(batchPromises)
      
      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          if (result.value) {
            notes.push(result.value)
          } else {
            console.warn(`Failed to load note ${batch[index].name}: empty content`)
          }
        } else {
          console.warn(`Failed to load note ${batch[index].name}:`, result.reason)
        }
      })

      // Throttle between batches
      if (i + batchSize < fileInfos.length) {
        await this.apiLimiter.wait()
      }
    }

    return notes
  }

  /**
   * Load individual note content
   */
  async loadNoteContent(fileInfo) {
    try {
      const contentResponse = await this.apiRequest(`${this.API_BASE}/files/${fileInfo.id}`, {
        alt: 'media'
      })

      const content = await contentResponse.text()

      const noteData = (typeof content === 'string')
        ? JSON.parse(content)
        : (content && typeof content === 'object')
          ? content
          : null

      if (!noteData) {
        throw new Error('Unexpected note content format')
      }

      return {
        ...noteData,
        cloudFileId: fileInfo.id,
        lastModified: new Date(fileInfo.modifiedTime)
      }
    } catch (error) {
      console.error(`Failed to load note content for ${fileInfo.name}:`, error)
      return null
    }
  }

  /**
   * Delete note from Google Drive with attachment cleanup
   */
  async deleteNote(noteId, cloudFileId = null, noteContent = null) {
    // If note content is not provided, try to fetch it from Google Drive for attachment cleanup
    let finalNoteContent = noteContent
    let finalCloudFileId = cloudFileId
    
    // If no cloudFileId provided, try to find it using the noteId
    if (!finalCloudFileId) {
      try {
        console.log('🔍 GoogleDriveAPI: No cloudFileId provided, searching for file by noteId:', noteId)
        finalCloudFileId = await this.getNoteFileId(noteId)
        if (finalCloudFileId) {
          console.log('✅ GoogleDriveAPI: Found cloudFileId using noteId:', finalCloudFileId)
        }
      } catch (error) {
        console.warn('⚠️ GoogleDriveAPI: Failed to find cloudFileId using noteId:', error)
      }
    }
    
    // If we still don't have content or cloudFileId, try to fetch from Google Drive
    if (!finalNoteContent && finalCloudFileId) {
      try {
        console.log('🔍 GoogleDriveAPI: Fetching note content for attachment cleanup before deletion')
        const noteData = await this.apiRequest(`${this.API_BASE}/files/${finalCloudFileId}`, {
          alt: 'media'
        })
        
        // Parse the note data to extract content
        const parsedNote = (typeof noteData === 'string') ? JSON.parse(noteData) : noteData
        finalNoteContent = parsedNote?.content || ''
        console.log('✅ GoogleDriveAPI: Retrieved note content for attachment cleanup')
      } catch (error) {
        console.warn('⚠️ GoogleDriveAPI: Failed to fetch note content for attachment cleanup:', error)
        // Continue without content - some attachments may be orphaned but note will still be deleted
      }
    }
    
    return this.addToSyncQueue({
      type: 'delete_note',
      data: { noteId, cloudFileId: finalCloudFileId, noteContent: finalNoteContent },
      priority: 'normal'
    })
  }

  /**
   * Add operation to sync queue for batching and throttling
   */
  async addToSyncQueue(operation) {
    console.log(`📥 GoogleDriveAPI: Adding operation to sync queue: ${operation.type}`)
    console.log(`🔑 GoogleDriveAPI: Access token available: ${!!this.accessToken}`)
    
    if (!this.accessToken) {
      console.error('❌ GoogleDriveAPI: Cannot queue operation - no access token available!')
      console.error('🔍 GoogleDriveAPI: Operation details:', operation)
      throw new Error('GoogleDriveAPI not initialized - no access token available')
    }
    
    this.syncQueue.push({
      ...operation,
      id: `${operation.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      attempts: 0
    })

    console.log(`📋 GoogleDriveAPI: Queue now has ${this.syncQueue.length} operations`)

    // Process queue if not already syncing
    if (!this.isSyncing) {
      this.processSyncQueue()
    }
  }

  /**
   * Process sync queue with intelligent batching
   */
  async processSyncQueue() {
    if (this.isSyncing || this.syncQueue.length === 0) {
      if (this.isSyncing) {
        console.log('⏳ GoogleDriveAPI: Sync already in progress, skipping queue processing')
      }
      if (this.syncQueue.length === 0) {
        console.log('📭 GoogleDriveAPI: Sync queue is empty')
      }
      return
    }

    this.isSyncing = true
    console.log(`🔄 GoogleDriveAPI: Processing ${this.syncQueue.length} queued operations`)

    try {
      while (this.syncQueue.length > 0) {
        const operation = this.syncQueue.shift()

        try {
          const result = await this.executeOperation(operation)
          console.log(`✅ GoogleDriveAPI: Completed ${operation.type}`)
          
          // If this was a save operation and we got a file ID, emit an event to update the note
          if (operation.type === 'save_note' && result) {
            console.log('🔗 GoogleDriveAPI: Emitting noteSaved event with cloudFileId:', result)
            
            // Emit event to notify NotesContext that the note has been saved with a cloudFileId
            // Include both temp and permanent IDs for proper synchronization
            window.dispatchEvent(new CustomEvent('noteSavedToCloud', {
              detail: {
                tempNoteId: operation.data.tempNoteId || operation.data.noteId, // Original temp ID if available
                permanentNoteId: operation.data.noteId, // Final permanent ID
                cloudFileId: result,
                note: operation.data
              }
            }))
          } else if (operation.type === 'save_note') {
            // Even if no file ID, still emit event for ID synchronization
            console.log('🔗 GoogleDriveAPI: Emitting noteSaved event without cloudFileId for ID sync')
            
            window.dispatchEvent(new CustomEvent('noteSavedToCloud', {
              detail: {
                tempNoteId: operation.data.tempNoteId || operation.data.noteId,
                permanentNoteId: operation.data.noteId,
                cloudFileId: null,
                note: operation.data
              }
            }))
          }
        } catch (error) {
          console.error(`❌ GoogleDriveAPI: Failed ${operation.type}:`, error)
          
          // Retry logic
          if (await this.retryManager.shouldRetry(operation, error)) {
            operation.attempts++
            this.syncQueue.push(operation)
            console.log(`🔄 GoogleDriveAPI: Retrying ${operation.type} (attempt ${operation.attempts})`)
          }
        }

        // Respect API rate limits
        await this.apiLimiter.wait()
      }
    } finally {
      this.isSyncing = false
      console.log('✅ GoogleDriveAPI: Sync queue processing complete')
    }
  }

  /**
   * Execute individual sync operation
   */
  async executeOperation(operation) {
    switch (operation.type) {
      case 'save_note':
        const fileId = await this.executeSaveNote(operation.data)
        console.log('✅ GoogleDriveAPI: Save operation completed, file ID:', fileId)
        
        // Update the note data with the cloudFileId if it's a new note
        if (!operation.data.cloudFileId && fileId) {
          operation.data.cloudFileId = fileId
          console.log('🔗 GoogleDriveAPI: Added cloudFileId to note:', fileId)
        }
        
        return fileId
      case 'delete_note':
        return this.executeDeleteNote(operation.data)
      default:
        throw new Error(`Unknown operation type: ${operation.type}`)
    }
  }

  /**
   * Process note content to replace data URLs with image references
   */
  processNoteContentForCloud(note) {
    const processedNote = { ...note }
    
    if (processedNote.content) {
      // Replace data URLs with image references in the content
      processedNote.content = processedNote.content.replace(
        /<img[^>]+src="data:image\/[^;]+;base64,[^"]+"[^>]*>/g,
        (match) => {
          // Extract the data URL from the img tag
          const srcMatch = match.match(/src="([^"]+)"/)
          if (srcMatch) {
            const dataUrl = srcMatch[1]
            // Extract the base64 data
            const base64Match = dataUrl.match(/data:image\/([^;]+);base64,(.+)/)
            if (base64Match) {
              const mimeType = base64Match[1]
              const base64Data = base64Match[2]
              
              // Create a unique reference for this image using a generic extension
              // This avoids MIME type vs file extension mismatches
              const imageRef = `image://cloud-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.img`
              
              // Replace the src with the reference
              return match.replace(/src="[^"]+"/, `src="${imageRef}"`)
            }
          }
          return match
        }
      )
      
      console.log('🖼️ GoogleDriveAPI: Processed note content for cloud storage (replaced data URLs with references)')
    }
    
    return processedNote
  }

  /**
   * Execute note save operation
   */
  async executeSaveNote(note) {
    const notesFolderId = this.folderCache.get('NOTES')
    
    // Ensure we have a valid noteId
    if (!note.noteId) {
      console.error('❌ GoogleDriveAPI: Note missing noteId!', note)
      throw new Error('Cannot save note without noteId')
    }
    
    // Use note content directly (no processing needed with new approach)
    const processedNote = note
    
    const fileName = `${note.noteId}.json`
    const noteData = JSON.stringify(processedNote, null, 2)

    console.log('💾 GoogleDriveAPI: Executing save note operation')
    console.log('📂 GoogleDriveAPI: Notes folder ID:', notesFolderId)
    console.log('📄 GoogleDriveAPI: File name:', fileName)
    console.log('🗂️ GoogleDriveAPI: App folder ID:', this.appFolderId)
    console.log('🗄️ GoogleDriveAPI: Folder cache contents:', Object.fromEntries(this.folderCache))

    if (!notesFolderId) {
      console.error('❌ GoogleDriveAPI: Notes folder ID is missing! Attempting to reinitialize folder structure...')
      console.log('🔍 GoogleDriveAPI: Current app folder ID:', this.appFolderId)
      console.log('🔍 GoogleDriveAPI: Current folder cache:', Object.fromEntries(this.folderCache))
      
      await this.ensureAppFolderStructure()
      const refreshedNotesFolderId = this.folderCache.get('NOTES')
      console.log('🔄 GoogleDriveAPI: Refreshed notes folder ID:', refreshedNotesFolderId)
      console.log('🔄 GoogleDriveAPI: Refreshed app folder ID:', this.appFolderId)
      console.log('🔄 GoogleDriveAPI: Refreshed folder cache:', Object.fromEntries(this.folderCache))
      
      if (!refreshedNotesFolderId) {
        throw new Error('Failed to initialize Google Drive folder structure - notes folder ID still missing')
      }
    }

    // Use the refreshed folder ID or the original one
    const finalNotesFolderId = this.folderCache.get('NOTES')
    
    console.log('📂 GoogleDriveAPI: Final notes folder ID for upload:', finalNotesFolderId)
    
    if (!finalNotesFolderId) {
      console.error('❌ GoogleDriveAPI: Cannot proceed with upload - notes folder ID is still missing!')
      throw new Error('Google Drive folder structure initialization failed - cannot save note')
    }

    // CRITICAL FIX: Always try to find existing file by noteId to prevent duplicates
    let existingFileId = note.cloudFileId
    
    if (!existingFileId) {
      try {
        console.log('🔍 GoogleDriveAPI: No cloudFileId found, searching for existing file by noteId:', note.noteId)
        existingFileId = await this.getNoteFileId(note.noteId)
        if (existingFileId) {
          console.log('✅ GoogleDriveAPI: Found existing file by noteId for update:', existingFileId)
        }
      } catch (error) {
        console.log('🔍 GoogleDriveAPI: No existing file found by noteId, will create new file')
      }
    }
    
    if (existingFileId) {
      // Update existing file
      console.log('📝 GoogleDriveAPI: Updating existing file with ID:', existingFileId)
      const updateResponse = await this.apiRequest(`${this.UPLOAD_BASE}/files/${existingFileId}`, null, {
        method: 'PATCH',
        body: noteData,
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (!updateResponse.ok) {
        throw new Error(`Failed to update note: ${updateResponse.status}`)
      }
      
      console.log('✅ GoogleDriveAPI: Note updated successfully')
      
      // Return the existing cloudFileId for updates
      return existingFileId
    } else {
      // Create new file using multipart upload (proper format for Google Drive API)
      const metadata = {
        name: fileName,
        parents: [finalNotesFolderId],
        mimeType: 'application/json'
      }
      
      console.log('📝 GoogleDriveAPI: Creating new file with metadata:', metadata)

      // Create proper multipart request body
      const boundary = 'boundary_' + Date.now().toString(36)
      const partDelimiter = `--${boundary}\r\n`
      const closeDelimiter = `\r\n--${boundary}--`

      let body = ''
      // Metadata part
      body += partDelimiter
      body += 'Content-Type: application/json; charset=UTF-8\r\n\r\n'
      body += JSON.stringify(metadata) + '\r\n'
      // Media part
      body += partDelimiter
      body += 'Content-Type: application/json; charset=UTF-8\r\n\r\n'
      body += noteData
      body += closeDelimiter

      const createResponse = await this.apiRequest(`${this.UPLOAD_BASE}/files?uploadType=multipart`, null, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': `multipart/related; boundary=${boundary}`
        }
      })
      
      if (!createResponse.ok) {
        throw new Error(`Failed to create note: ${createResponse.status}`)
      }
      
      // Extract the file ID from the response
      const responseData = await createResponse.json()
      const fileId = responseData.id
      
      if (!fileId) {
        console.error('❌ GoogleDriveAPI: Created file response missing ID:', responseData)
        throw new Error('Google Drive response missing file ID')
      }
      
      console.log('✅ GoogleDriveAPI: Note created successfully with file ID:', fileId)
      
      // Return the file ID so it can be added to the note as cloudFileId
      return fileId
    }
  }

  /**
   * Upload attachment file to Google Drive
   */
  async uploadAttachment(file) {
    const attachmentsFolderId = this.folderCache.get('ATTACHMENTS')
    
    if (!attachmentsFolderId) {
      console.error('❌ GoogleDriveAPI: Attachments folder ID is missing! Attempting to reinitialize folder structure...')
      await this.ensureAppFolderStructure()
      const refreshedAttachmentsFolderId = this.folderCache.get('ATTACHMENTS')
      
      if (!refreshedAttachmentsFolderId) {
        throw new Error('Failed to initialize Google Drive folder structure - attachments folder ID missing')
      }
    }

    const finalAttachmentsFolderId = this.folderCache.get('ATTACHMENTS')
    
    // Generate unique filename using actual filename + timestamp to avoid conflicts
    const fileExtension = file.name.split('.').pop() || ''
    const baseFileName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
    const timestamp = Date.now()
    const uniqueFileName = `${baseFileName}_${timestamp}.${fileExtension}`
    
    console.log('📎 GoogleDriveAPI: Uploading attachment:', file.name, 'as', uniqueFileName)
    
    // Create new file using multipart upload for binary data
    const metadata = {
      name: uniqueFileName,
      parents: [finalAttachmentsFolderId],
      mimeType: file.type || 'application/octet-stream'
    }
    
    // Read file as ArrayBuffer for binary upload
    const fileBuffer = await file.arrayBuffer()
    
    // Create proper multipart request body for binary file
    const boundary = 'boundary_' + Date.now().toString(36)
    const partDelimiter = `--${boundary}\r\n`
    const closeDelimiter = `\r\n--${boundary}--`

    // Convert metadata to JSON
    const metadataJson = JSON.stringify(metadata)
    
    // Create multipart body as Uint8Array for binary data
    const encoder = new TextEncoder()
    const decoder = new TextDecoder()
    
    let bodyParts = []
    
    // Metadata part
    bodyParts.push(encoder.encode(partDelimiter))
    bodyParts.push(encoder.encode('Content-Type: application/json; charset=UTF-8\r\n\r\n'))
    bodyParts.push(encoder.encode(metadataJson + '\r\n'))
    
    // File data part
    bodyParts.push(encoder.encode(partDelimiter))
    bodyParts.push(encoder.encode(`Content-Type: ${file.type || 'application/octet-stream'}\r\n\r\n`))
    bodyParts.push(new Uint8Array(fileBuffer))
    bodyParts.push(encoder.encode(closeDelimiter))
    
    // Combine all parts into single Uint8Array
    const totalLength = bodyParts.reduce((sum, part) => sum + part.length, 0)
    const body = new Uint8Array(totalLength)
    let offset = 0
    for (const part of bodyParts) {
      body.set(part, offset)
      offset += part.length
    }

    const response = await this.apiRequest(`${this.UPLOAD_BASE}/files?uploadType=multipart`, null, {
      method: 'POST',
      body,
      headers: {
        'Content-Type': `multipart/related; boundary=${boundary}`
      }
    })
    
    const result = await response.json()
    
    console.log('✅ GoogleDriveAPI: Attachment uploaded successfully:', result)
    console.log('✅ GoogleDriveAPI: Result ID:', result.id)
    console.log('✅ GoogleDriveAPI: Result keys:', Object.keys(result || {}))
    
    if (!result || !result.id) {
      throw new Error('Upload failed: No file ID returned from Google Drive')
    }
    
    return {
      id: result.id,
      fileName: file.name,
      originalFileName: file.name,
      actualFileName: uniqueFileName, // The actual filename in Google Drive
      fileType: file.type,
      fileSize: file.size,
      cloudFileId: result.id,
      googleDriveUrl: `https://drive.google.com/file/d/${result.id}/view`
    }
  }

  /**
   * Download attachment from Google Drive
   */
  async downloadAttachment(cloudFileId) {
    try {
      console.log('📎 GoogleDriveAPI: Downloading attachment:', cloudFileId)
      
      // Get file metadata first
      const fileInfoResponse = await this.apiRequest(`${this.API_BASE}/files/${cloudFileId}`, {
        fields: 'name,mimeType,size'
      })
      
      // Parse the response if it's a Response object
      const fileInfo = fileInfoResponse.files ? fileInfoResponse : await fileInfoResponse.json()
      console.log('📎 GoogleDriveAPI: File info:', fileInfo)
      
      // Download file content as blob
      const response = await fetch(`${this.API_BASE}/files/${cloudFileId}?alt=media`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          // Use automatic 401 handling from token refresh service
          console.log('🔄 GoogleDriveAPI: 401 in download, using automatic token refresh')
          console.log('🔍 GoogleDriveAPI: Response details:', { status: response.status, statusText: response.statusText })
          console.log('🔍 GoogleDriveAPI: Calling handleGoogleAPI401 with request:', { url: `${this.API_BASE}/files/${cloudFileId}?alt=media` })
          
          try {
            const newAccessToken = await googleTokenRefreshService.handleGoogleAPI401()
            
            console.log('🔍 GoogleDriveAPI: handleGoogleAPI401 returned new access token:', {
              hasToken: !!newAccessToken,
              tokenLength: newAccessToken?.length,
              tokenPrefix: typeof newAccessToken === 'string' ? newAccessToken.substring(0, 20) + '...' : 'undefined'
            })
            
            if (newAccessToken) {
              // Update local token for future requests
              // Use TokenManager to set the token so expiry is also set
              tokenManager.setGoogleDriveToken(newAccessToken)
              this.accessToken = newAccessToken
              
              // Retry the download with the new token
              console.log('🔄 GoogleDriveAPI: Retrying download with new token...')
              const retryResponse = await fetch(`${this.API_BASE}/files/${cloudFileId}?alt=media`, {
                headers: {
                  'Authorization': `Bearer ${newAccessToken}`
                }
              })
              
              if (retryResponse.ok) {
                console.log('✅ GoogleDriveAPI: Download succeeded after token refresh')
                const blob = await retryResponse.blob()
                
                console.log('✅ GoogleDriveAPI: Attachment downloaded successfully:', fileInfo.name)
                
                const result = {
                  blob,
                  fileName: fileInfo.name,
                  fileType: fileInfo.mimeType,
                  fileSize: fileInfo.size
                }
                console.log('📎 GoogleDriveAPI: Returning (retry):', result)
                return result
              } else {
                console.error('❌ GoogleDriveAPI: Retry download failed:', retryResponse.status, retryResponse.statusText)
                throw new Error(`HTTP error! status: ${retryResponse.status}`)
              }
            } else {
              console.error('❌ GoogleDriveAPI: No access token received from refresh')
              throw new Error('Token refresh failed - no access token received')
            }
          } catch (refreshError) {
            console.error('❌ GoogleDriveAPI: Token refresh failed during download:', refreshError)
            throw refreshError
          }
        }
        
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const blob = await response.blob()
      
      console.log('✅ GoogleDriveAPI: Attachment downloaded successfully:', fileInfo.name)
      
      const result = {
        blob,
        fileName: fileInfo.name,
        fileType: fileInfo.mimeType,
        fileSize: fileInfo.size
      }
      console.log('📎 GoogleDriveAPI: Returning (normal):', result)
      return result
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Failed to download attachment:', error)
      throw error
    }
  }

  /**
   * Delete attachment from Google Drive
   */
  async deleteAttachment(cloudFileId) {
    try {
      console.log('🗑️ GoogleDriveAPI: Deleting attachment:', cloudFileId)
      
      await this.apiRequest(`${this.API_BASE}/files/${cloudFileId}`, null, {
        method: 'DELETE'
      })
      
      console.log('✅ GoogleDriveAPI: Attachment deleted successfully')
      return true
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Failed to delete attachment:', error)
      throw error
    }
  }

  /**
   * Extract cloud attachment IDs from note content
   * @private
   */
  extractCloudAttachmentIds(noteContent) {
    if (!noteContent || typeof noteContent !== 'string') {
      return []
    }
    
    // Find all cloud attachment links: #cloud-attachment-{fileId}
    const attachmentPattern = /#cloud-attachment-([a-zA-Z0-9_-]+)/g
    const attachmentIds = []
    let match
    
    while ((match = attachmentPattern.exec(noteContent)) !== null) {
      attachmentIds.push(match[1])
    }
    
    console.log('🔍 GoogleDriveAPI: Found cloud attachment IDs:', attachmentIds)
    return attachmentIds
  }

  /**
   * Clean up orphaned attachments for a note
   * @private
   */
  async cleanupNoteAttachments(noteContent) {
    const attachmentIds = this.extractCloudAttachmentIds(noteContent)
    
    if (attachmentIds.length === 0) {
      console.log('📎 GoogleDriveAPI: No cloud attachments to clean up')
      return
    }
    
    console.log(`🧹 GoogleDriveAPI: Cleaning up ${attachmentIds.length} cloud attachments`)
    
    // Delete each attachment from Google Drive
    const cleanupPromises = attachmentIds.map(async (fileId) => {
      try {
        await this.deleteAttachment(fileId)
        console.log(`✅ GoogleDriveAPI: Deleted attachment: ${fileId}`)
      } catch (error) {
        console.warn(`⚠️ GoogleDriveAPI: Failed to delete attachment ${fileId}:`, error)
        // Don't throw - continue with other deletions
      }
    })
    
    await Promise.allSettled(cleanupPromises)
    console.log(`🧹 GoogleDriveAPI: Completed attachment cleanup for note`)
  }

  /**
   * Execute note delete operation with attachment cleanup
   */
  async executeDeleteNote({ noteId, cloudFileId, noteContent }) {
    if (!cloudFileId) {
      console.log('⚠️ GoogleDriveAPI: No cloudFileId available for deletion, skipping cloud deletion')
      return { success: true, message: 'Note not found in Google Drive, skipping cloud deletion' }
    }

    console.log('🗑️ GoogleDriveAPI: Starting note deletion with attachment cleanup')
    
    // First, clean up any cloud attachments referenced in the note
    if (noteContent) {
      try {
        await this.cleanupNoteAttachments(noteContent)
      } catch (error) {
        console.warn('⚠️ GoogleDriveAPI: Attachment cleanup failed, continuing with note deletion:', error)
      }
    }

    // Move note to deleted folder instead of permanent deletion
    // Ensure folders are initialized
    if (!this.folderCache.get('DELETED')) {
      await this.ensureAppFolderStructure()
    }
    const deletedFolderId = this.folderCache.get('DELETED')
    
    // Get current parents to remove from notes folder
    const fileMeta = await this.apiRequest(`${this.API_BASE}/files/${cloudFileId}`, {
      fields: 'parents'
    })
    const currentParents = Array.isArray(fileMeta?.parents) ? fileMeta.parents.join(',') : ''
    
    // Move by updating parents via query params
    const result = await this.apiRequest(`${this.API_BASE}/files/${cloudFileId}`, {
      addParents: deletedFolderId,
      removeParents: currentParents
    }, {
      method: 'PATCH',
      body: JSON.stringify({})
    })
    
    console.log('✅ GoogleDriveAPI: Note deletion completed with attachment cleanup')
    return result
  }

  /**
   * Build URL with query parameters
   */
  buildUrl(endpoint, params = {}) {
    let url = endpoint
    
    // If endpoint doesn't start with http, it's a relative path
    if (!endpoint.startsWith('http')) {
      // Check if this is an upload endpoint
      if (endpoint.includes('uploadType=multipart')) {
        url = `https://www.googleapis.com/upload/drive/v3${endpoint}`
      } else {
        url = `https://www.googleapis.com/drive/v3${endpoint}`
      }
    }
    
    // Add query parameters if provided
    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(params).toString()
      url += (url.includes('?') ? '&' : '?') + queryString
    }
    
    return url
  }

  /**
   * CRITICAL FIX: Ensure we always have the latest token before making API calls
   * This prevents the issue where the instance token is stale after token refresh
   */
  async ensureLatestToken() {
    try {
      const storedToken = localStorage.getItem('cloudAccessToken')
      if (!storedToken) {
        console.log('⚠️ GoogleDriveAPI: No token found in localStorage')
        return
      }
      
      // Check if instance token is different from stored token
      if (storedToken !== this.accessToken) {
        console.log('🔄 GoogleDriveAPI: Token sync needed - instance token differs from localStorage')
        console.log('🔍 GoogleDriveAPI: Instance token:', this.accessToken?.substring(0, 20) + '...')
        console.log('🔍 GoogleDriveAPI: Stored token:', storedToken.substring(0, 20) + '...')
        
        // Update instance token immediately
        this.updateAccessToken(storedToken)
        console.log('✅ GoogleDriveAPI: Token synchronized successfully')
      } else {
        console.log('✅ GoogleDriveAPI: Token already synchronized')
      }
      
      // Additional validation: ensure token is not empty or too short
      if (this.accessToken && this.accessToken.length < 10) {
        console.warn('⚠️ GoogleDriveAPI: Instance token seems invalid (too short), attempting refresh')
        try {
          const newToken = await tokenManager.emergencyRefreshGoogleDriveToken()
          if (newToken && typeof newToken === 'string' && newToken.length > 10) {
            this.updateAccessToken(newToken.trim())
            console.log('✅ GoogleDriveAPI: Invalid token replaced with fresh one')
          }
        } catch (error) {
          console.error('❌ GoogleDriveAPI: Failed to refresh invalid token:', error.message)
        }
      }
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Error in ensureLatestToken:', error)
    }
  }

  /**
   * Make authenticated API request to Google Drive
   */
  async apiRequest(url, params = {}, options = {}) {
    try {
      // CRITICAL FIX: Always ensure we have the latest token before making any API call
      await this.ensureLatestToken()
      
      // Wait for valid token if needed
      await this.waitForValidToken()
      
      // DEBUG: Log the token being used for this request
      console.log('🔍 GoogleDriveAPI: Using token for API request:', {
        hasToken: !!this.accessToken,
        tokenLength: this.accessToken?.length,
        tokenPrefix: typeof this.accessToken === 'string' ? this.accessToken.substring(0, 20) + '...' : 'undefined',
        tokenFromLocalStorage: localStorage.getItem('cloudAccessToken')?.substring(0, 20) + '...'
      })
      
      // CRITICAL: Check if localStorage has a different token than the instance
      const storedToken = localStorage.getItem('cloudAccessToken')
      if (storedToken && storedToken !== this.accessToken) {
        console.log('🔄 GoogleDriveAPI: localStorage token differs from instance token, updating instance')
        console.log('🔍 GoogleDriveAPI: Old instance token:', this.accessToken?.substring(0, 20) + '...')
        console.log('🔍 GoogleDriveAPI: New stored token:', storedToken.substring(0, 20) + '...')
        // Use TokenManager to set the token so expiry is also set
        tokenManager.setGoogleDriveToken(storedToken)
        this.accessToken = storedToken
      }
      
      // PROACTIVE FIX: Check if current token is about to expire and refresh proactively
      if (this.accessToken && this.shouldProactivelyRefreshToken()) {
        console.log('🔄 GoogleDriveAPI: Proactively refreshing token before API call')
        try {
          const newToken = await tokenManager.emergencyRefreshGoogleDriveToken()
          if (newToken && typeof newToken === 'string') {
            // Use TokenManager to set the token so expiry is also set
            tokenManager.setGoogleDriveToken(newToken.trim())
            this.accessToken = newToken.trim()
            const newRefreshToken = localStorage.getItem('cloudRefreshToken')
            if (newRefreshToken) {
              this.refreshToken = newRefreshToken
            }
            console.log('✅ GoogleDriveAPI: Token proactively refreshed')
          }
        } catch (error) {
          console.warn('⚠️ GoogleDriveAPI: Proactive token refresh failed, continuing with current token:', error.message)
        }
      }
      
      // CRITICAL AGGRESSIVE FIX: Force refresh tokens that are older than 10 minutes regardless of expiry
      // This addresses cases where Google invalidates tokens much earlier than our calculated expiry
      if (this.accessToken && localStorage.getItem('googleDriveTokenExpiry')) {
        const expiryTime = new Date(localStorage.getItem('googleDriveTokenExpiry')).getTime()
        const currentTime = Date.now()
        const tokenAge = currentTime - (expiryTime - (45 * 60 * 1000)) // 45 minutes is our calculated lifetime
        
        if (tokenAge > (10 * 60 * 1000)) { // 10 minutes
          console.log('🔄 GoogleDriveAPI: Token is older than 10 minutes, forcing refresh regardless of expiry')
          try {
            const newToken = await tokenManager.emergencyRefreshGoogleDriveToken()
            if (newToken && typeof newToken === 'string') {
              // Use TokenManager to set the token so expiry is also set
              tokenManager.setGoogleDriveToken(newToken.trim())
              this.accessToken = newToken.trim()
              const newRefreshToken = localStorage.getItem('cloudRefreshToken')
              if (newRefreshToken) {
                this.refreshToken = newRefreshToken
              }
              console.log('✅ GoogleDriveAPI: Token forcefully refreshed due to age')
            }
          } catch (error) {
            console.warn('⚠️ GoogleDriveAPI: Force refresh due to age failed, continuing with current token:', error.message)
          }
        }
      }
      
      // CRITICAL: Ensure we have an expiry set for the current token
      if (this.accessToken && !localStorage.getItem('googleDriveTokenExpiry')) {
        console.log('⚠️ GoogleDriveAPI: No expiry found for current token, setting default expiry (45 minutes from now)')
        // Set a default expiry of 45 minutes from now to prevent immediate 401s
        const defaultExpiry = new Date(Date.now() + (45 * 60 * 1000)).toISOString()
        localStorage.setItem('googleDriveTokenExpiry', defaultExpiry)
        console.log('✅ GoogleDriveAPI: Set default expiry:', defaultExpiry)
      }
      
      // Build final URL with query parameters
      const finalUrl = this.buildUrl(url, params)
      
      // Prepare request options
      const finalOptions = {
        method: options.method || 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      }
      
      console.log('🔍 GoogleDriveAPI: Making API request:', {
        url: finalUrl,
        method: finalOptions.method,
        hasAccessToken: !!this.accessToken,
        accessTokenLength: this.accessToken?.length,
        accessTokenPrefix: typeof this.accessToken === 'string' ? this.accessToken.substring(0, 20) + '...' : 'undefined'
      })
      
      // Make the request
      const response = await fetch(finalUrl, finalOptions)
      
             if (response.status === 401) {
         console.log('❌ GoogleDriveAPI: 401 Unauthorized, attempting token refresh...')
         
         try {
           console.log('🔍 GoogleDriveAPI: Calling emergency token refresh...')
           // Use TokenManager's emergency refresh to bypass rate limiting
           const newAccessToken = await tokenManager.emergencyRefreshGoogleDriveToken()
           console.log('🔍 GoogleDriveAPI: handleGoogleAPI401 returned:', {
             hasToken: !!newAccessToken,
             tokenType: typeof newAccessToken,
             tokenLength: newAccessToken?.length,
             tokenPrefix: typeof newAccessToken === 'string' ? newAccessToken.substring(0, 20) + '...' : 'undefined'
           })
           
           if (newAccessToken && typeof newAccessToken === 'string') {
             // Validate token format
             const cleanToken = newAccessToken.trim()
             
             if (!cleanToken || cleanToken.length < 10) {
               console.error('❌ GoogleDriveAPI: Invalid token format:', {
                 token: cleanToken,
                 length: cleanToken?.length,
                 type: typeof cleanToken
               })
               throw new Error('Token refresh returned invalid token format')
             }
             
             // CRITICAL FIX: Update the internal accessToken property IMMEDIATELY
             // Use TokenManager to set the token so expiry is also set
             tokenManager.setGoogleDriveToken(cleanToken)
             this.accessToken = cleanToken
             
             // Also update the refreshToken if available
             const newRefreshToken = localStorage.getItem('cloudRefreshToken')
             if (newRefreshToken) {
               this.refreshToken = newRefreshToken
             }
             console.log('✅ GoogleDriveAPI: Token refreshed, retrying original request...')
             console.log('🔍 GoogleDriveAPI: New token length:', cleanToken?.length)
             console.log('🔍 GoogleDriveAPI: New token prefix:', cleanToken.substring(0, 20) + '...')
             console.log('🔍 GoogleDriveAPI: Current instance accessToken:', this.accessToken?.substring(0, 20) + '...')
             
             // Small delay to ensure token propagation
             await new Promise(resolve => setTimeout(resolve, 100))
             
             // Verify token is properly stored
             const storedToken = localStorage.getItem('cloudAccessToken')
             console.log('🔍 GoogleDriveAPI: Stored token verification:', {
               hasStoredToken: !!storedToken,
               storedTokenLength: storedToken?.length,
               storedTokenPrefix: storedToken?.substring(0, 20) + '...',
               matchesNewToken: storedToken === cleanToken
             })
             
             // Create a fresh options object with the new token
             const retryOptions = {
               ...finalOptions,
               headers: {
                 ...finalOptions.headers,
                 'Authorization': `Bearer ${cleanToken}`
               }
             }
             
             console.log('🔍 GoogleDriveAPI: Retry request headers:', retryOptions.headers)
             console.log('🔍 GoogleDriveAPI: Retry URL:', finalUrl)
             console.log('🔍 GoogleDriveAPI: Retry method:', retryOptions.method)
             console.log('🔍 GoogleDriveAPI: Authorization header value:', retryOptions.headers.Authorization)
             console.log('🔍 GoogleDriveAPI: Authorization header length:', retryOptions.headers.Authorization?.length)
             
             const retryResponse = await fetch(finalUrl, retryOptions)
             
             if (!retryResponse.ok) {
               console.error('❌ GoogleDriveAPI: Retry request failed:', retryResponse.status, retryResponse.statusText)
               console.error('❌ GoogleDriveAPI: Retry response headers:', Object.fromEntries(retryResponse.headers.entries()))
               throw new Error(`Google Drive API request failed: ${retryResponse.status}`)
             }
             
             console.log('✅ GoogleDriveAPI: Retry request succeeded')
             return retryResponse
           } else {
             console.error('❌ GoogleDriveAPI: Token refresh returned invalid access token:', {
               token: newAccessToken,
               type: typeof newAccessToken,
               isString: typeof newAccessToken === 'string'
             })
             throw new Error('Token refresh failed - invalid access token received')
           }
         } catch (refreshError) {
           console.error('❌ GoogleDriveAPI: Token refresh failed:', refreshError)
           throw new Error('Failed to refresh Google Drive token. Please reconnect.')
         }
       }
      
      if (!response.ok) {
        throw new Error(`Google Drive API request failed: ${response.status}`)
      }
      
      return response
      
    } catch (error) {
      console.error(`❌ GoogleDriveAPI: Request to ${url} failed:`, error)
      throw error
    }
  }

  /**
   * Check sync queue status
   */
  getSyncStatus() {
    return {
      queueLength: this.syncQueue.length,
      isSyncing: this.isSyncing,
      lastSync: this.lastSyncTime
    }
  }

  /**
   * Resolve Google Drive file ID for a note by noteId
   */
  async getNoteFileId(noteId) {
    try {
      await this.ensureAppFolderStructure()
      const notesFolderId = this.folderCache.get('NOTES')
      if (!notesFolderId) return null

      const fileName = `${noteId}.json`
      const resultResponse = await this.apiRequest(`${this.API_BASE}/files`, {
        q: `name='${fileName}' and parents in '${notesFolderId}' and mimeType='application/json' and trashed = false`,
        fields: 'files(id, name)',
        pageSize: 1
      })
      const result = await resultResponse.json()
      const id = result?.files?.[0]?.id || null
      if (!id) {
        console.log(`🔍 GoogleDriveAPI: No file found for noteId ${noteId}`)
      }
      return id
    } catch (e) {
      console.warn('⚠️ GoogleDriveAPI: getNoteFileId failed:', e?.message || e)
      return null
    }
  }
  
  /**
   * Cleanup resources
   */
  cleanup() {
    try {
      // Clear token check interval
      if (this.tokenCheckInterval) {
        clearInterval(this.tokenCheckInterval)
        this.tokenCheckInterval = null
        console.log('🧹 GoogleDriveAPI: Token check interval cleared')
      }
      
      // Clear folder initialization promise
      if (this.folderInitPromise) {
        this.folderInitPromise = null
        console.log('🧹 GoogleDriveAPI: Folder init promise cleared')
      }
      
      console.log('✅ GoogleDriveAPI: Cleanup completed')
    } catch (error) {
      console.error('❌ GoogleDriveAPI: Cleanup failed:', error)
    }
  }
}

/**
 * API Rate Limiter
 * Implements intelligent rate limiting for Google Drive API
 */
class ApiLimiter {
  constructor() {
    this.requests = []
    this.maxRequestsPerSecond = 10 // Conservative limit
    this.burstLimit = 100 // Burst allowance
  }

  async wait() {
    const now = Date.now()
    
    // Clean old requests (older than 1 second)
    this.requests = this.requests.filter(time => now - time < 1000)
    
    if (this.requests.length >= this.maxRequestsPerSecond) {
      const oldestRequest = Math.min(...this.requests)
      const waitTime = 1000 - (now - oldestRequest)
      
      if (waitTime > 0) {
        console.log(`⏳ GoogleDriveAPI: Rate limiting, waiting ${waitTime}ms`)
        await new Promise(resolve => setTimeout(resolve, waitTime))
      }
    }
    
    this.requests.push(now)
  }
}

/**
 * Retry Manager
 * Handles intelligent retry logic with exponential backoff
 */
class RetryManager {
  constructor() {
    this.maxRetries = 3
    this.baseDelay = 1000 // 1 second
  }

  async shouldRetry(operation, error) {
    if (operation.attempts >= this.maxRetries) return false

    // Don't retry on client errors (4xx)
    if (error.status >= 400 && error.status < 500) return false

    // Retry on server errors and network issues
    const delay = this.baseDelay * Math.pow(2, operation.attempts)
    await new Promise(resolve => setTimeout(resolve, delay))
    
    return true
  }
}

// Export singleton instance
export default new GoogleDriveAPI() 